import { UpdateOrderParams } from './../../types/order.type';
import { CommonModule } from '@angular/common';
import { Component, OnInit, WritableSignal, signal, input, inject, InputSignal, model, computed, output, Signal, ViewChild, ElementRef } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { SubscriptionsService } from '../../../settings/account/subscriptions/subscriptions.service';
import { NotificationService } from '../../notification/notification.service';
import { Subscription } from '../../../settings/account/subscriptions/subscriptions.types';
import { OnlyNumberDirective } from '../../directives/only-number.directive';
import { getProductSubscriptioninfo, openModalSkipProduct, openModalUnskipProduct } from '../../common/subscriptions.utils';
import { ModalContentService } from '../../modal-content/modal-content.service';
import { finalize, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FirebaseCrudService } from '../../firebase-crud.service';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { ProductType } from '../../types/common.enums';
import { updateFirebaseOrderAndSubTrigger } from '../../common/order.utils';
import { ResolutionService } from '../../resolution.service';
import { setUpBundleChangesLog } from '../../common/bundle.utils';
import { ModalContentTypes } from '../../constants/modal-content-types';
import { toObservable } from '@angular/core/rxjs-interop';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    OnlyNumberDirective,
    EmptyMessageComponent,
    NgbTooltipModule,
  ]
})

export class ProductCartPreview implements OnInit {

  @ViewChild('whatInside') whatInside!: ElementRef;

  signalsStoreService = inject(SignalsStoreService)
  orderService = inject(OrderService)
  #router = inject(Router);
  #subscriptionsService = inject(SubscriptionsService);
  #notificationService = inject(NotificationService);
  #modalContentService = inject(ModalContentService);
  #firebaseCrudService = inject(FirebaseCrudService);
  #resoluctionService = inject(ResolutionService);

  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity = model(1);
  // DEV-11183: el tooltip del badge se abre con tap en móvil y con hover en escritorio.
  isMobile = computed(() => this.#resoluctionService.isMobile());
  availableQuantities: Signal<{ val: number, name: string }[]> = computed(() => this.#setUpAvailableQuantities());

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      this.isMoreThanTen.set(product.quantity >= 10);
      return product;
    }
  })

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  isInOrder = input(false);

  isSkipped = input(false);

  isSubscription = input(false);

  isPreorder = input(false);

  showUpdateButton = computed(() => this.isMoreThanTen() && (this.previousValue !== +(this.newQuantity() || 0)))

  isMoreThanTen = signal(false);

  odooOrder = computed(() => this.orderService.odooOrder());

  showProductStatusTag = environment.config.flows.order.showProductStatusTag;

  deliveryInfo: InputSignal<any> = input.required();

  outUpdateOrder = output<void>();

  orderId = computed(() => this.odooOrder()?.id ?? 0);

  totalPrice = computed(() => this.#setupProductTotalPrice());

  // Pre-discount unit + line total, used to render the strikethrough when the
  // customer is on a discounted pricelist. Backend exposes `originalPrice` on
  // each line (see smc_api_front sale_order_v3 `line_original_price`).
  originalUnitPrice: Signal<number> = computed(() => {
    const p = this.product();
    const orig = Number(p?.originalPrice ?? 0);
    return orig;
  });

  hasPricelistDiscount: Signal<boolean> = computed(() => {
    const p = this.product();
    const orig = Number(p?.originalPrice ?? 0);
    const price = Number(p?.price ?? 0);
    return orig > 0 && orig > price;
  });

  originalTotalPrice: Signal<number> = computed(() => {
    const p = this.product();
    const orig = Number(p?.originalPrice ?? 0);
    const qty = Number(p?.quantity ?? 0);
    return orig * qty;
  });

  isSmallMobile = computed(() => this.#resoluctionService.isSmallMobile());

  productVariantText = computed(() => {
    const variantName = this.product()?.variant?.attribute?.name || '';
    const variantValue = this.product()?.variant?.attribute?.value?.name || '';
    if (variantName && variantValue)
      return `${variantName}: ${variantValue}`
    return null
  });

  productPackageText = computed(() => {
    const packageName = this.product()?.package?.name || '';
    if (packageName) return `Package: ${packageName}`;
    return null;
  });

  productFrequencyText = computed(() => {
    const variant = this.productVariantText();
    const packageName = this.productPackageText();
    const smallMobile = this.isSmallMobile();
    const frequency = this.product().subscription?.frequency?.name || '';
    if (!frequency) return null;

    if (!smallMobile && (variant || packageName)) return `| ${frequency}`;
    return frequency;
  });

  #showCartPreview$ = toObservable(this.signalsStoreService.showCartPreview)

  bundleItems = computed(() => {
    const items = this.product()?.bundle?.items || [];
    return items.sort((a: any, b: any) => {
      if (a.isPremiumAddon && !b.isPremiumAddon) return -1;
      if (!a.isPremiumAddon && b.isPremiumAddon) return 1;
      return 0;
    });
  });

  ngOnInit(): void {
    this.isInputValid = true;

    this.#showCartPreview$.subscribe((val) => {
      if (val) return;
      this.whatInside?.nativeElement?.classList?.remove('show');
    });
  }

  prevRemoveCartProduct() {
    const odooOrder = this.odooOrder();

    if (!odooOrder?.id)
      return;

    this.orderService
      .deleteProductOrder(+odooOrder.id, this.product().lineId)
      .pipe(finalize(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService)))
      .subscribe()
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  goToCustomizeBox() {
    if (!this.product()?.bundle?.items?.length) {
      this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
        title: '',
        textContent: `
        <p class="h5-bold">Your box has not been published yet!</p>
        <p class="">You will be able to customize your box <strong>Friday after 5pm</strong>.</p>
        `,
        confirmButtonText: 'I Understand',
      })
      return;
    }
    this.signalsStoreService.showCartPreview.set(false);
    this.#router.navigate([`/shop/custom-box/${this.productKey()}/${this.product().id}`]);
  }

  unskip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalUnskipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  skip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalSkipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  #getSubscriptionInfoByProduct(product: any): Subscription | null {
    const subscriptions = this.signalsStoreService.subscriptions();
    return getProductSubscriptioninfo(subscriptions, product.id);
  }

  onQuantityChange() {
    if (!this.newQuantity())
      return;

    if (this.newQuantity() === 10)
      return this.isMoreThanTen.set(true);

    this.#updateProduct();
  }

  validateQuantity() {
    if (!this.newQuantity())
      return this.#notificationService.show({ text: 'Please enter a valid quantity', type: 'error' });

    // DEV-11172: pre-order items can only be increased. Block any qty
    // below what's already on the order (e.g. via the free-text input
    // that appears once qty >= 10).
    if (this.isPreorder() && (this.newQuantity() || 0) < (this.product()?.quantity || 1)) {
      this.newQuantity.set(this.product()?.quantity || 1);
      return this.#notificationService.show({
        text: 'Pre-order items can only be increased. Contact customer service for other changes.',
        type: 'error',
      });
    }

    this.#updateProduct();
  }

  #updateProduct() {

    const copy = this.product();
    copy.quantity = this.newQuantity();
    copy.bundleQuantityChanged = true;

    const obj: UpdateOrderParams = {
      orderId: this.orderId(),
      product: { ...copy, variantId: copy.variant.id, packageId: copy.package?.id || null } as any,
      type: this.productKey(),
      bundleChangesLog: this.product()?.isBundle ? setUpBundleChangesLog(this.product(), this.newQuantity()) : undefined
    };

    this.orderService
      .updateOrder(obj, this.productKey() === ProductType.subscription)
      .pipe(tap(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService)))
      .subscribe();
  }

  #setUpAvailableQuantities(): { val: number, name: string }[] {
    const res = [] as { val: number, name: string }[];
    const stockInfo = this.product()?.stockInfo?.[0] || null;

    // Search for variant quantity in the order:
    let productQuantity = 1;

    const odooOrder = this.odooOrder();
    if (odooOrder) {
      const commonProduct = odooOrder.products.common.find((product: any) => product.variantId === this.product().variantId);
      if (commonProduct) {
        productQuantity = commonProduct.quantity;
      }

      const subscriptionProduct = odooOrder.products.subscription.find((product: any) => product.variantId === this.product().variantId);
      if (subscriptionProduct) {
        productQuantity += subscriptionProduct.quantity;
      }
    }

    let purchaseLimit = this.product()?.purchaseLimit || 0;

    if (!stockInfo) return res;

    const stock = stockInfo.stock + (stockInfo.limit * -1)
    const totalAvailableStock = stock < 0 ? 0 : stock;

    let available = totalAvailableStock + productQuantity;

    if (purchaseLimit) {

      const packQuantity = this.product().package?.quantity || 1
      if (packQuantity > 1)
        purchaseLimit = Math.floor(purchaseLimit / packQuantity)

      const remainingPurchaseLimit = purchaseLimit - productQuantity;
      const currentProductQuantity = this.product().quantity || 1;
      const normalizedRemainingPurchaseLimit = remainingPurchaseLimit < 0 ? 0 : remainingPurchaseLimit;
      available = currentProductQuantity + normalizedRemainingPurchaseLimit;
    }

    if (available > 10) available = 10;

    if (this.product().isBundle)
      available = 10;

    // DEV-11172: for pre-order lines the customer can only INCREASE
    // the quantity, never decrease it. The dropdown starts at the
    // current cart quantity so lower values aren't selectable.
    const minSelectable = this.isPreorder() ? (this.product()?.quantity || 1) : 1;

    if ((available) <= 10) {
      for (let index = minSelectable; index <= available; index++) {
        res.push({ val: index, name: (index === 10 ? '+10' : index.toString()) });
      }
    }

    return res
  }

  #setupProductTotalPrice() {
    const p = this.product();
    if (!p.isBundle) return p.totalPrice;

    const totalPrice = this.orderService.calculateProductTotalPrice(p);
    return totalPrice;
  }
}
